<template>
  <div class="flex flex-col gap-4 p-6 pb-8 2xl:p-9">
    <RfPromptHeader
      :id="prompt.id"
      v-model:filtersData="filtersData"
      v-model:date="date"
      :appDevices="currApp?.path_filter?.dimension_filters?.device_categories"
      :appLanguages="currApp?.path_filter?.dimension_filters?.languages"
      :appDeviceTypes="currApp?.flags?.device_type_analytics ? getCustomDeviceEntries(prompt) : []"
      :disabled="userStore.isDisabledWithMessage"
      :lockDatepickerPreset="
        dataCard?.selectedTab?.key === 'activity' ? METRICS_PERIODS.last_seven_days.value : false
      "
      lockDatepickerPresetMessage="Only the last 7 days are available in Live view"
      :name="prompt.name"
      :description="prompt.description"
      :statusDisabled="!!prompt.sequence_id"
      :defaultDate="defaultDate"
      :segments="prompt?.sequence_segments || prompt?.segments"
      :status="promptStatus"
      :type="type"
      :skeleton="skeleton"
      :startDate="prompt.start_date"
      :customDevicesEnabled="customDevicesEnabled"
      :isPushNotification="isPushNotification"
      :disabledActions="disabledActions"
      :possibleStatuses="statusActionsForPrompt(prompt, currApp.timezone_offset)"
      :actionList="promptActionList"
      @editCustomDevices="() => detailModals.showCustomDevicesModal()"
      @back="goBack"
      @update:status="v => (statusUpdate = { prompt: prompt, status: v })"
      @update:date="fetchMetrics"
      @update:filtersData="fetchMetrics"
    />
    <div class="flex flex-3 flex-col gap-4">
      <RfPollingBanner v-if="promptStore.isPollingMetrics" />
      <RfPushPromptTip v-if="isPushNotification" :prompt="prompt" :promptStatus="promptStatus" />
      <RfDashboardCard
        v-if="shouldShowDashboardCard"
        ref="dataCard"
        :title="dashboardCardData.title"
        :style="{
          height:
            !biggerOrEqual5xl && dataCard?.selectedTab?.key === 'activity'
              ? '900px'
              : biggerOrEqualLg
                ? '488px'
                : '570px',
        }"
        @change="e => e.key === 'activity' && (liveFirstRender = true)"
      >
        <template #[dashboardCardData.template]>
          <RfPromptData
            ref="chart"
            :displayMetrics="displayMetrics"
            :prompt="prompt"
            :skeleton="dataSkeleton"
            :metrics="metrics"
            :preset="date?.preset?.value"
          />
        </template>

        <template #activity>
          <RfLive
            v-if="displayLiveFeed && liveFirstRender"
            :key="promptFilters"
            :active="dataCard?.selectedTab?.key === 'activity'"
            :promptFilters="promptFilters"
            class="rf-prompt--live [&_.rf-input]:bg-input-background! h-full w-full min-w-px"
            :dateFilter="date"
            :promptFilter="prompt"
            :getFilters="getFilters"
            style="--rf-prompt-data--height: 345px"
          />
        </template>
      </RfDashboardCard>

      <RfPromptAdditionalData
        :mainChartHasData="chart?.hasData"
        :skeleton="dataSkeleton"
        :showInteraction="!isInvisible && !isPushNotification"
        :showCustomGoalInteraction="!isInvisible && !isPushNotification && hasCustomGoal"
        :showSurvey="isSurvey"
        :metrics="metrics"
        :displayMetrics="displayMetrics"
        :displayMetricsCustom="displayMetricsCustom"
        :surveyMetrics="surveyMetrics"
        :surveyInputs="surveyInputs"
        :preset="date?.preset?.value"
      />
    </div>
    <RfPromptDetails
      id="details"
      :prompt="prompt"
      :isPushNotification="isPushNotification"
      :segments="prompt?.sequence_segments || prompt?.segments"
      :appId="currApp.id"
      :guide="guide"
      :triggers="prompt?.triggers"
      :promptTags="prompt?.tags"
      :appTags="tagsStore.tags"
      :isDisabledRole="userStore.isDisabledWithMessage"
      :isDisabledByActiveExperiment="isDisabledByActiveExperiment"
      :creativeLink="creativeLink"
      :isInvisible="isInvisible"
      :skeleton="skeleton"
      @editSegments="() => detailModals.showSegmentsModal()"
      @editTriggers="() => detailModals.showTriggersModal()"
      @editLimits="() => detailModals.showLimitsModal()"
      @editSchedule="() => detailModals.showScheduleModal()"
      @editInterval="() => detailModals.showIntervalModal()"
      @editMetadata="() => detailModals.showMetadataModal()"
      @editEngagementCategory="() => detailModals.showEngagementCategoryModal()"
      @editConsentCategories="() => detailModals.showConsentCategoriesModal()"
      @createTag="createTagAndAssignToPrompt"
      @removeTag="removeTagFromPrompt"
      @assignTag="assignTagToPrompt"
      @copyPromptId="copyToClipboard(prompt.id, 'Prompt id copied to the clipboard')"
      @importFromFigma="figmaImportModalRef?.open()"
    />
    <RfPromptCustomGoal
      :skeleton="skeleton"
      :disabled="userStore.isDisabledWithMessage || isDisabledByActiveExperiment"
      :duration="prompt.actions?.rf_settings_custom_goal_expire_hours"
      :customFilter="prompt.custom_filter"
      :data="totalCustomGoals"
      @add="() => customGoalModal.show()"
      @delete="resetCustomGoal"
    />
    <template v-if="!isPushNotification">
      <RfPromptActions
        :skeleton="skeleton"
        :disabled="userStore.isDisabledWithMessage || isDisabledByActiveExperiment"
        :prompt="prompt"
        :currLanguage="promptStore.currLanguage"
        :currAppConnectors="appsStore.currAppConnectors"
        :providers="currApp.providers"
        :appClientActions="appClientActions"
        :actionGroupConnectorActionsGroupedByType="actionGroupConnectorActionsGroupedByType"
        @add="action => connectorModal.show?.(action)"
        @updateActions="updateActionsOrder"
        @editClientAction="v => connectorModal.editArgs?.(v)"
        @deleteClientAction="v => connectorModal.deleteAction?.(v)"
        @editIntegration="v => connectorModal.editIntegration?.(v)"
        @deleteIntegration="v => connectorModal.deleteAction?.(v)"
      />
      <RfPromptExperiments
        :skeleton="skeleton"
        :disableCreate="
          userStore.isDisabledWithMessage ||
          isPaywall ||
          isDisabledByActiveExperiment ||
          isDisabledByDraftExperiment ||
          isLocalized
        "
        :disableStatusChange="userStore.isDisabledWithMessage || isLocalized"
        :experiments="experimentsStore.experiments"
        :appId="currApp.id"
        :promptId="prompt.id"
        :isInvisible="isInvisible"
        :hasCustomGoal="hasCustomGoal"
        :customGoalInteraction="experimentsStore.customGoalInteraction"
        :paywallMessage="isPaywall || ''"
        @add="() => newExperimentModal.show()"
        @status="updateExperiment"
      />
    </template>
    <RfPromptHistory
      :skeleton="skeleton"
      :activities="promptStore.activities || []"
      :connectorErrors="prompt.connector_errors"
      :auditableType="isGuide ? 'Sequence' : 'Path'"
      :auditableId="isGuide ? prompt.sequence_id : prompt.id"
      @open="e => connectorErrorModal.show?.(e)"
    />
    <RfRetentionPriority
      v-if="!isPlacement"
      ref="priorityModal"
      :filterFn="retentionsFilterFn"
      :currRetention="prompt"
    />
    <RfBaseModal
      ref="resetModal"
      title="Are you sure you want to reset?"
      message="Reset will result in all currently targeted users becoming eligible to receive this prompt, regardless of previous interactions. All limits and frequency caps will be reset."
      width="580"
      secondaryText="Reset"
      @secondary="submitReset"
    />
    <RfPromptModals
      ref="modals"
      v-model:editItem="editItem"
      v-model:deleteItem="deleteItem"
      v-model:cloneItem="cloneItem"
      v-model:statusUpdate="statusUpdate"
      :currApp="currApp"
      :currUser="userStore.currUser"
      :isSuperAdmin="userStore.isCurrentSuperAdmin"
      :isCompanyAdmin="userStore.isCurrentAdmin"
      :timezone="currApp.timezone_offset"
      :onSubmitRename="
        v =>
          promptStore.updatePrompt({
            appId: currApp.id,
            pathId: editItem.id,
            modelPath: { ...editItem, ...v },
          })
      "
      @getPathGroups="allPromptStore.getPromptGroups"
      @delete="submitDelete"
      @clone="submitClone"
      @cloneAsVariation="submitCloneAsVariation"
      @status="submitStatusUpdate"
    />
    <RfPromptDetailModals
      ref="detailModals"
      :isDisabledRole="userStore.isDisabledWithMessage"
      :isDisabledByActiveExperiment="isDisabledByActiveExperiment"
      :isPushNotification="isPushNotification"
      :prompt="prompt"
      @updatePrompt="updatePrompt"
      @updateSegments="updateSegments"
      @updateTriggers="updateTriggers"
    />
    <RfBaseModal
      ref="customGoalModal"
      title="Add a custom conversion goal"
      width="500"
      @primary="updateCustomGoal"
    >
      <template #body>
        <RfGoal ref="goal" :model="prompt" />
      </template>
    </RfBaseModal>
    <RfBaseModal
      ref="newExperimentModal"
      title="Create a new experiment"
      width="640"
      @primary="saveExperiment"
    >
      <template #body>
        <v-text-field
          v-model.trim="experimentName"
          variant="outlined"
          density="compact"
          label="Name"
          persistentPlaceholder
          name="experimentName"
          required
          hideDetails
          class="mt-3 mb-4"
          placeholder="Experiment's name"
        ></v-text-field>
      </template>
    </RfBaseModal>
    <RfExperimentStatusModal
      v-if="experimentToUpdate"
      ref="statusExperimentModal"
      :experimentToUpdate="experimentToUpdate"
      :showPromptMessage="!prompt?.is_enabled"
      @primary="submitExperiment"
      @close="experimentToUpdate = null"
    />
    <RfPromptActionsModals
      ref="connectorModal"
      :app="currApp"
      :prompt="prompt"
      :currLanguage="promptStore.currLanguage"
      :currAppConnectors="appsStore.currAppConnectors"
      :appClientActions="appClientActions"
      :clientActionObjects="clientActions"
      :clientActions="actionGroupConnectorActionsGroupedByType.ClientAction"
      @updateClientActions="updateClientActions"
      @deleteAction="deleteAction"
    />
    <RfConnectorErrorModal ref="connectorErrorModal" :prompt="prompt" />
    <RfFigmaImportModal
      ref="figmaImportModalRef"
      :app="currApp"
      :promptId="prompt.id"
      :pathGroupId="prompt.path_group?.id"
      mode="import"
      @done="init"
    />
  </div>
</template>

<script>
import RfPromptModals from "@/blocks/RfModals/RfPromptModals.vue";
import RfPromptHeader from "@/blocks/RfPrompt/RfPromptHeader.vue";
import RfPollingBanner from "@/components/RfPollingBanner.vue";
import { usePromptsStore } from "@/pinia/promptsStore";
import { useToastsStore } from "@/pinia/toastsStore";
import copyToClipboard from "@/utils/CopyToClipboard";
import { exportSinglePromptToCsv } from "@/utils/CsvUtils";
import { getCustomDeviceEntries, getPromptDeviceType } from "@/utils/getDeviceName";
import {
  getGuideLink,
  getPromptsLink,
  getZoneLink,
  getPromptLink,
  getGuidePromptLink,
  getExperimentLink,
} from "@/utils/getLink";
import getPromptStatus from "@/utils/prompts/getPromptStatus";
import {
  isCustomGoalExists,
  isInvisible,
  isLatestExperimentActive,
  isLatestExperimentUnstarted,
  isLocalized,
  isOverlay,
  isPaywall,
  isPlacement,
  isPushNotification,
  isSurvey,
  disabledActionsForPrompt,
  statusActionsForPrompt,
} from "@/utils/prompts/promptHelpers";
import { computed, onMounted, ref } from "vue";
import { useDynamicTitle } from "@/utils/composables/useDynamicTitle";
import RfPromptDetails from "@/blocks/RfPrompt/RfPromptDetails/RfPromptDetails.vue";
import RfPromptDetailModals from "@/blocks/RfModals/RfPromptDetailModals.vue";
import RfLive from "@/blocks/RfLive/RfLive.vue";
import RfPromptCustomGoal from "@/blocks/RfPrompt/RfPromptCustomGoal.vue";
import RfPromptHistory from "@/blocks/RfPrompt/RfPromptHistory.vue";
import RfPromptActions from "@/blocks/RfPrompt/RfPromptActions/RfPromptActions.vue";
import RfPromptActionsModals from "@/blocks/RfPrompt/RfPromptActions/RfPromptActionsModals.vue";
import RfPromptExperiments from "@/blocks/RfPrompt/RfPromptExperiments/RfPromptExperiments.vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import RfGoal from "@/components/RfPathCreate/RfGoal.vue";
import { cloneDeep } from "lodash-es";
import {
  getSurveyInputs,
  getTotals,
  getDisplayCustomMetrics,
  getDisplayMetrics,
} from "@/utils/metricsHelpers";
import { PATH_CUSTOM_METRIC_SURVEY } from "@/utils/constants/CustomMetricsConstants";
import { groupBy } from "@/utils/arrayUtils";
import { isRedirectAction } from "@/utils/prompts/promptActionHelpers";
import RfPromptData from "@/blocks/RfPrompt/RfPromptData.vue";
import { useUserSettings } from "@/utils/composables/useUserSettings";
import MetricsUtils from "@/utils/MetricsUtils";
import RfConnectorErrorModal from "@/blocks/RfModals/RfConnectorErrorModal.vue";
import RfFigmaImportModal from "@/components/RfFigmaImportModal.vue";
import { useTagsStore } from "@/pinia/tagsStore";
import ApiTags from "@/apis/ApiTags";
import RfPromptAdditionalData from "@/blocks/RfPrompt/RfPromptAdditionalData.vue";
import { PromptTypes } from "@/utils/constants/PromoTypesConstants";
import RfDashboardCard from "@/components/cards/RfDashboardCard.vue";
import RfPushPromptTip from "@/components/RfPushPromptTip.vue";
import { biggerOrEqual5xl, biggerOrEqualLg } from "@/utils/composables/useBreakpoint";
import { useLegacyTmpStore } from "@/pinia/legacyTmpStore";
import { useExperimentsStore } from "@/pinia/experimentsStore";
import { useActionsStore } from "@/pinia/actionsStore";
import { useTriggersStore } from "@/pinia/triggersStore";
import { usePromptStore } from "@/pinia/promptStore";
import { useAppsStore } from "@/pinia/appsStore";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";
import { useUserStore } from "@/pinia/userStore";
import RfRetentionPriority from "../RfRetentionPriority.vue";
import { METRICS_PERIODS } from "@/utils/constants/MetricsConstants";
import { debounce } from "@/utils/debounce";
import StatusesConstants from "@/utils/constants/PromptStatusesConstants";
import {
  shouldShowEngagementCategoryNudge,
  clearNudgeDismissal,
} from "@/utils/engagementCategoryNudge";
import { useFilterData } from "@/utils/composables/useFilterData";
import RfRenameIcon from "@/components/icons/RfRenameIcon.vue";
import RfResetIcon from "@/components/icons/RfResetIcon.vue";
import RfCloneIcon from "@/components/icons/RfCloneIcon.vue";
import RfPriorityIcon from "@/components/icons/RfPriorityIcon.vue";
import RfDownloadIcon from "@/components/icons/RfDownloadIcon.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfExperimentStatusModal from "@/blocks/RfExperiments/RfExperimentStatusModal.vue";

export default {
  name: "RfPromptView",
  components: {
    RfPromptHeader,
    RfPollingBanner,
    RfPromptModals,
    RfRetentionPriority,
    RfBaseModal,
    RfPromptDetails,
    RfPromptDetailModals,
    RfLive,
    RfPromptCustomGoal,
    RfGoal,
    RfPromptHistory,
    RfPromptExperiments,
    RfPromptActionsModals,
    RfPromptActions,
    RfPromptData,
    RfConnectorErrorModal,
    RfFigmaImportModal,
    RfPromptAdditionalData,
    RfDashboardCard,
    RfPushPromptTip,
    RfExperimentStatusModal,
  },
  setup: () => {
    const { getUserSetting, setUserSetting } = useUserSettings();
    const chart = ref({ hasData: false });
    const resetModal = ref();
    const priorityModal = ref();
    const date = ref();
    const editItem = ref(null);
    const deleteItem = ref(null);
    const cloneItem = ref(null);
    const statusUpdate = ref({ status: null, prompt: null });
    const modals = ref();
    const detailModals = ref();
    const customGoalModal = ref();
    const newExperimentModal = ref();
    const statusExperimentModal = ref();
    const goal = ref();
    const experimentName = ref("");
    const experimentToUpdate = ref(null);
    const skeleton = ref(true);
    const dataSkeleton = ref(true);
    const connectorModal = ref();
    const connectorErrorModal = ref();
    const defaultDate = ref("last_seven_days");
    const figmaImportModalRef = ref();
    const tagsStore = useTagsStore();
    const appsStore = useAppsStore();
    const currApp = computed(() => appsStore.app);
    const {
      filtersData,
      filterDataKeys: promptFilters,
      getDimensionFilters: getFilters,
    } = useFilterData();

    onMounted(() => {
      if (!tagsStore.tags?.length) tagsStore.getTags(currApp.value?.id);
    });

    return {
      chart,
      date,
      editItem,
      deleteItem,
      cloneItem,
      statusUpdate,
      modals,
      copyToClipboard,
      getCustomDeviceEntries,
      getPromptDeviceType,
      priorityModal,
      resetModal,
      detailModals,
      customGoalModal,
      goal,
      currApp,
      newExperimentModal,
      statusExperimentModal,
      experimentName,
      experimentToUpdate,
      connectorModal,
      defaultDate,
      connectorErrorModal,
      figmaImportModalRef,
      skeleton,
      dataSkeleton,
      tagsStore,
      biggerOrEqual5xl,
      biggerOrEqualLg,
      appsStore,
      liveFirstRender: ref(false),
      dataCard: ref(),
      toastsStore: useToastsStore(),
      promptsStore: usePromptsStore(),
      userStore: useUserStore(),
      experimentsStore: useExperimentsStore(),
      actionsStore: useActionsStore(),
      triggersStore: useTriggersStore(),
      promptStore: usePromptStore(),
      allPromptStore: useAllPromptsStore(),
      legacyTmpStore: useLegacyTmpStore(),
      METRICS_PERIODS,
      statusActionsForPrompt,
      filtersData,
      promptFilters,
      getFilters,
      getUserSetting,
      setUserSetting,
    };
  },
  data() {
    return {
      updateSynchronousConnectors: debounce(v => {
        const prompt = {
          ...this.prompt,
          filter: {
            ...this.prompt.filter,
            connectors: {
              ...this.prompt.filter.connectors,
              settings: { ...this.prompt.filter.connectors.settings, synchronous: v },
            },
          },
        };
        this.updatePrompt(prompt);
      }, 200),
    };
  },
  computed: {
    promptActionList() {
      return [
        {
          "if":
            this.userStore.isCurrentSuperAdmin ||
            (!!this.currApp.flags?.connector_action_status_reporting && this.userStore.isAdmin),
          "modelValue": this.prompt.filter?.connectors?.settings?.synchronous,
          "type": "switch",
          "class": "flex-reverse w-full flex-col items-start! px-4 py-3",
          "title": "Connector Action Status Reporting",
          "onUpdate:modelValue": v => this.updateSynchronousConnectors(v),
        },
        {
          icon: RfRenameIcon,
          title: "Rename",
          onClick: () => (this.editItem = this.prompt),
        },
        {
          if: !!this.prompt.last_published_date,
          icon: RfResetIcon,
          title: "Reset",
          onClick: () => this.resetModal.show?.(),
        },
        {
          icon: RfCloneIcon,
          title: "Clone",
          onClick: () => (this.cloneItem = this.prompt),
        },
        {
          if: !this.isPlacement,
          icon: RfPriorityIcon,
          title: "Priority",
          onClick: () => this.priorityModal.show?.(),
        },
        {
          icon: RfDownloadIcon,
          title: "Export to CSV",
          onClick: () =>
            exportSinglePromptToCsv(
              { ...this.prompt, data: this.metrics },
              this.date.preset?.value,
            ),
        },
        {
          icon: RfDeleteIcon,
          title: "Delete",
          onClick: () => (this.deleteItem = [this.prompt]),
        },
      ];
    },
    dashboardCardData() {
      return {
        title: this.displayLiveFeed
          ? [
              { key: "performance", value: "Performance" },
              { key: "activity", value: "Live View" },
            ]
          : "Performance",
        template: this.displayLiveFeed ? "performance" : "body",
      };
    },
    clientActions() {
      return this.actionsStore.clientActions || [];
    },
    prompt() {
      return this.promptStore.prompt || {};
    },
    surveyMetrics() {
      return (
        this.promptStore.customMetrics?.[PATH_CUSTOM_METRIC_SURVEY]?.[this.date?.preset?.value]
          ?.agg_data?.[2] || {}
      );
    },
    surveyInputs() {
      return getSurveyInputs(this.prompt);
    },
    metrics() {
      return this.promptStore.promptMetricsId === this.$route.params.pid
        ? this.promptStore.promptMetrics
        : {};
    },
    creativeLink() {
      return `/apps/${this.currApp.id}/retentions/${this.prompt.id}/creative`;
    },
    actionGroupConnectorActionsGroupedByType() {
      return groupBy(this.prompt.action_group_connector_actions || [], "connectable_type");
    },
    appClientActions() {
      return this.clientActions
        .filter(action => action.action_type === "website_action")
        .map(action => ({ text: action.name, value: action.id }));
    },
    isPushNotification() {
      return isPushNotification(this.prompt);
    },
    isPlacement() {
      return isPlacement(this.prompt.path_group?.path_type);
    },
    promptStatus() {
      return getPromptStatus(this.prompt, this.currApp.timezone_offset);
    },
    type() {
      return getPromptDeviceType(this.prompt, this.currApp.flags?.custom_devices, {
        delimiter: " ",
      });
    },
    guide() {
      if (!this.prompt.sequence_id || !this.prompt.sequence_name) return null;
      return {
        id: this.prompt.sequence_id,
        name: this.prompt.sequence_name,
        type: this.prompt.sequence_type,
        order: this.prompt.sequence_order,
        link: getGuideLink(this.prompt.sequence_id, this.currApp.id),
      };
    },
    isGuide() {
      return !!this.prompt.sequence_id;
    },
    isDisabledByActiveExperiment() {
      return this.userStore.isCurrentSuperAdmin
        ? false
        : isLatestExperimentActive({ path: this.prompt }) && "Disabled due to active experiment";
    },
    isDisabledByDraftExperiment() {
      return (
        isLatestExperimentUnstarted({ path: this.prompt }) && "Disabled due to existing experiment"
      );
    },
    isLocalized() {
      return isLocalized(this.prompt) && "Disabled for localized prompts";
    },
    isPaywall() {
      return isPaywall(this.prompt) && "Experiments aren't available for paywall prompts";
    },
    hasCustomGoal() {
      return isCustomGoalExists(this.prompt);
    },
    isInvisible() {
      return isInvisible(this.prompt);
    },
    totalCustomGoals() {
      if (this.isInvisible)
        return (
          (this.hasCustomGoal
            ? this.metrics?.custom_goals_accepted?.[this.date?.preset?.value]?.uniques
            : this.metrics?.goals?.[this.date?.preset?.value]?.uniques) || 0
        );
      return getTotals(this.metrics, "custom_goals_seen", this.date?.preset?.value);
    },
    isSurvey() {
      return isSurvey(this.prompt);
    },
    customDevicesEnabled() {
      return this.currApp?.flags?.custom_devices && this.prompt?.device_type === "custom_defined";
    },
    displayMetrics() {
      return getDisplayMetrics(this.prompt);
    },
    displayMetricsCustom() {
      return getDisplayCustomMetrics(this.prompt, this.displayMetrics);
    },
    displayLiveFeed() {
      return ![PromptTypes.email.value, PromptTypes.scheduled_push.value].includes(
        this.prompt?.path_type,
      );
    },
    shouldShowDashboardCard() {
      return ![StatusesConstants.ready.type, StatusesConstants.pending.type].includes(
        this.promptStatus.type,
      );
    },
    disabledActions() {
      return disabledActionsForPrompt(this.prompt);
    },
    languages() {
      if (!this.isLocalized) return [];
      const translations = this.prompt.parsed_translations;
      if (!translations) return [];
      const items = [];
      Object.keys(translations).forEach(language => {
        const values = translations[language];
        if (values.default) {
          items.unshift({ title: `${language} (default)`, value: language });
        } else {
          items.push({ title: language, value: language });
        }
      });
      return items;
    },
  },
  watch: {
    "prompt.id": function (v) {
      if (!v || this.$route.hash !== "#details") return;
      this.$router.push({ hash: "" });
      setTimeout(() => (location.href = `${this.$route.fullPath}#details`));
    },
  },
  created() {
    this.promptStore.$reset();
    const preset = MetricsUtils.All_Periods.find(
      ({ value }) => value === this.getUserSetting("promotionsDateRange"),
    )?.value;

    if (preset) this.defaultDate = preset;
  },
  async mounted() {
    useDynamicTitle(() => {
      const name = this.prompt?.name;
      const context = this.prompt?.sequence_name || "Prompts";
      return name && `${name} | ${context}`;
    });

    this.$nextTick(async () => {
      this.skeleton = true;
      await this.init();
      if (!this.prompt.id) {
        return this.$router.replace({ path: `/apps/${this.currApp.id}/dashboard` });
      }
      this.skeleton = false;

      // Check if we should show engagement category nudge modal on page load
      if (shouldShowEngagementCategoryNudge(this.currApp, this.prompt, "prompt")) {
        this.$nextTick(() => {
          this.detailModals?.showEngagementCategoryModal();
        });
      }
    });
    if (!this.clientActions.length) this.actionsStore?.getClientActions(this.currApp.id);
    if (this.currApp.flags?.sample_data_enabled == true) console.warn("Sample data enabled!");
  },
  methods: {
    async fetchMetrics() {
      if (this.metrics[this.date.preset.value] || this.dataCard?.selectedTab?.key === "activity")
        return;

      this.dataSkeleton = true;
      const attrs = {
        appId: this.currApp.id,
        id: this.$route.params.pid,
        params: {
          metric_periods: [
            {
              period: this.date.preset.value,
              ...(this.date.preset.value === "custom" && {
                date_range: [
                  this.date.startDate.format("YYYY-MM-DD"),
                  this.date.endDate.format("YYYY-MM-DD"),
                ],
              }),
            },
          ],
          ...this.getFilters(),
        },
      };

      await Promise.all([
        this.promptStore.getPathMetrics(attrs),
        this.isSurvey
          ? this.promptStore.getPathCustomMetrics({
              ...attrs,
              params: { ...attrs.params, metric_keys: [PATH_CUSTOM_METRIC_SURVEY] },
            })
          : () => null,
      ]);

      this.dataSkeleton = false;

      if (this.date.preset.value !== "custom")
        this.setUserSetting("promotionsDateRange", this.date.preset.value);
    },
    async init() {
      const { pid: pathId, aid: appId } = this.$route.params;
      return Promise.all([
        this.promptStore.getPrompt({ appId, pathId }),
        this.experimentsStore.getExperiments(pathId),
        this.appsStore.getAppConnectors(appId),
      ]);
    },
    hasSameDeviceTypeAsCurrPrompt(item) {
      return item.device_type === this.prompt?.device_type;
    },
    hasSamePathTypeAsCurrPrompt(item) {
      return item.path_type === this.prompt?.path_type;
    },
    isPromptOverlay() {
      return isOverlay(this.prompt);
    },
    promoDisplayTypeVal(item) {
      return this.isPromptOverlay ? isOverlay(item) : this.hasSamePathTypeAsCurrPrompt(item);
    },
    retentionsFilterFn(item) {
      return this.hasSameDeviceTypeAsCurrPrompt(item) && this.promoDisplayTypeVal(item);
    },
    async submitStatusUpdate([prompt]) {
      try {
        await this.promptStore.updatePromptState({
          appId: this.currApp.id,
          pathId: this.prompt.id,
          stateData: {
            is_enabled: prompt.is_enabled,
            start_date: prompt.start_date,
            end_date: prompt.end_date,
          },
        });
        this.$refs.modals.closeStatusModal();
        this.toastsStore.create({ type: "success", body: "Status updated" });
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message });
      }
    },
    submitDelete() {
      const body = `Prompt${this.deleteItem.length > 1 ? "s" : ""} deleted`;
      this.promptsStore.bulkDelete(this.currApp.id, this.deleteItem).then(() => {
        this.$refs.modals.closeDeleteModal();
        this.toastsStore.create({ type: "success", body });
        this.goBack();
      });
    },
    async submitClone({
      targetAppId,
      targetAppName,
      pathGroupId,
      sequenceId,
      sequenceName,
      defaultCompany,
      engagementCategory = null,
    }) {
      let body = `Cloned ${this.cloneItem.name}`;
      let timeout;
      try {
        const path = await this.promptStore.clonePath({
          appId: this.currApp.id,
          pathId: this.cloneItem.id,
          targetAppId,
          pathGroupId,
          sequenceId,
          defaultCompany,
          engagementCategory,
        });
        this.$refs.modals.closeCloneModal();
        if (this.currApp.id === targetAppId) {
          if (path?.id) {
            const link = sequenceId
              ? getGuidePromptLink(path.id, sequenceId, this.currApp.id)
              : getPromptLink(path.id, this.currApp.id);
            this.$router.push(link);
            this.init();
          }
          if (sequenceId && sequenceName) {
            body += ` to "${sequenceName}"`;
            timeout = 5000;
          }
        } else {
          body += ` to ${targetAppName}`;
          timeout = 5000;
        }

        this.toastsStore.create({ type: "success", body, timeout });
      } catch (error) {
        this.toastsStore.create({ type: "error", body: error.message });
      }
    },
    async submitCloneAsVariation({ targetPathId }) {
      try {
        await this.promptStore.cloneAsVariation({
          appId: this.currApp.id,
          pathId: this.cloneItem.id,
          targetPathId,
        });
        this.toastsStore.create({
          type: "success",
          body: `Added "${this.cloneItem.name}" as a new variation`,
        });
        this.$refs.modals.closeCloneModal();
      } catch (error) {
        this.toastsStore.create({ type: "error", body: error.message });
      }
    },
    goBack() {
      if (this.$route.name === "Retention Detail")
        this.$router.push(getPromptsLink(null, this.currApp.id));
      if (this.$route.name === "Guide's Retention Detail")
        this.$router.push(getGuideLink(this.$route.params.sid, this.currApp.id));
      if (this.$route.name === "Placements's Retention Detail")
        this.$router.push(getZoneLink(this.$route.params.plid, this.currApp.id));
    },
    submitReset() {
      this.promptStore
        .resetPathGoals({
          appId: this.prompt.app_id,
          pathId: this.prompt.id,
        })
        .then(this.init)
        .then(() => {
          this.toastsStore.create({ type: "success", body: "Clicks reset successfully" });
          this.resetModal?.close();
        });
    },
    async updatePrompt(newPath, message) {
      try {
        await this.promptStore.updatePrompt({
          appId: this.currApp.id,
          pathId: this.prompt.id,
          modelPath: newPath,
        });
        this.customGoalModal?.close();
        this.detailModals.closeSegmentsModal();
        this.detailModals.closeLimitsModal();
        this.detailModals.closeScheduleModal();
        this.detailModals.closeIntervalModal();
        this.detailModals.closeCustomDevicesModal();
        this.detailModals.closeMetadataModal();
        this.detailModals.closeEngagementCategoryModal();
        this.detailModals.closeConsentCategoriesModal();
        this.connectorModal.close?.();

        // Clear nudge dismissal if engagement category was updated
        if (newPath.engagement_category) {
          clearNudgeDismissal("prompt", this.prompt.id);
        }

        this.toastsStore.create({
          type: "success",
          body: message || "Prompt updated successfully",
        });
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message });
      }
    },
    async updateSegments(newPath) {
      try {
        await this.promptStore.updatePromptSegment({
          appId: this.currApp.id,
          pathId: newPath.id,
          segments: newPath.segments,
        });
        this.detailModals.closeSegmentsModal();
        this.toastsStore.create({ type: "success", body: "Prompt updated successfully" });
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message });
      }
    },
    async updateTriggers({ newPath }) {
      try {
        await this.promptStore.updatePromptTrigger({
          appId: this.currApp.id,
          pathId: newPath.id,
          triggers: newPath.triggers,
        });

        this.detailModals.closeTriggersModal();
        this.toastsStore.create({ type: "success", body: "Prompt updated successfully" });
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message });
      }
    },
    async updateCustomGoal() {
      const newRetention = cloneDeep(this.prompt);
      this.goal.fillModel(newRetention);
      try {
        await this.promptStore.updatePromptGoal({
          appId: this.currApp.id,
          pathId: this.prompt.id,
          goalData: {
            custom_filter: newRetention.custom_filter,
            actions: {
              rf_settings_custom_goal_expire_hours:
                newRetention.actions.rf_settings_custom_goal_expire_hours,
              rf_settings_custom_goal_interval:
                newRetention.actions.rf_settings_custom_goal_interval,
            },
          },
        });
        this.customGoalModal?.close();
        this.toastsStore.create({ type: "success", body: "Prompt updated successfully" });
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message });
      }
    },
    async resetCustomGoal() {
      const newRetention = cloneDeep(this.prompt);
      try {
        await this.promptStore.updatePromptGoal({
          appId: this.currApp.id,
          pathId: this.prompt.id,
          goalData: {
            custom_filter: {},
            actions: {
              rf_settings_custom_goal_expire_hours:
                newRetention.actions.rf_settings_custom_goal_expire_hours,
              rf_settings_custom_goal_interval:
                newRetention.actions.rf_settings_custom_goal_interval,
            },
          },
        });
        this.toastsStore.create({ type: "success", body: "Prompt updated successfully" });
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message });
      }
    },
    async saveExperiment() {
      try {
        const modelExperiment = { ...this.legacyTmpStore.experiment };
        modelExperiment.algorithm = "weighed_sample";
        if (!this.experimentName) throw new Error("Experiment name can't be empty");
        modelExperiment.name = this.experimentName;
        modelExperiment.is_live_editable = false;
        modelExperiment.variations = [
          {
            name: "Control",
            traffic_percent: 0,
            is_control: true,
            is_baseline: false,
            is_visible: false,
            actions: {},
            action_group_connector_actions: [],
            triggers: [],
          },
          {
            name: "Original",
            traffic_percent: 0,
            is_control: false,
            is_baseline: true,
            is_visible: true,
            actions: this.prompt.actions,
            action_group_connector_actions: this.prompt.action_group_connector_actions,
            triggers: this.prompt.triggers,
          },
        ];
        const experiment = await this.experimentsStore.createExperiment({
          pathId: this.prompt.id,
          modelExperiment,
        });
        this.newExperimentModal.close();
        if (experiment) {
          this.toastsStore.create({ type: "success", body: "Experiment created successfully" });
          this.$router.replace({
            path: getExperimentLink(experiment.id, this.prompt.id, this.currApp.id),
          });
        } else {
          this.toastsStore.create({ type: "error", body: this.lastError });
        }
      } catch (e) {
        this.toastsStore.create({ type: "error", body: e.message });
      }
    },
    updateExperiment({ experiment, running }) {
      if (!running) {
        if (experiment.variations.reduce((acc, el) => acc + el.traffic_percent, 0) !== 100)
          return this.toastsStore.create({
            type: "error",
            body: "Your traffic percentages must add up to 100%",
          });
      }

      this.experimentToUpdate = experiment;
      this.$nextTick(() => this.statusExperimentModal.show());
    },
    async submitExperiment(pausePromo) {
      await this.experimentsStore.runExperiment({
        appId: this.currApp.id,
        pathId: this.prompt.id,
        expId: this.experimentToUpdate.id,
        isStart: !this.experimentToUpdate.is_active,
      });
      if (pausePromo) {
        await this.promptStore
          .updatePromptState({
            appId: this.currApp.id,
            pathId: this.prompt.id,
            stateData: { is_enabled: false },
          })
          .catch(() => null);
      }
      if (this.prompt.experiment?.id === this.experimentToUpdate.id) {
        this.prompt = {
          ...this.prompt,
          experiment: {
            ...this.prompt.experiment,
            is_active: !this.experimentToUpdate.is_active,
          },
        };

        this.promptStore.getPrompt({
          appId: this.$route.params.aid,
          pathId: this.$route.params.pid,
        });
      }
      this.statusExperimentModal.close();
    },

    updateClientActions(action) {
      let index = -1;
      if (action.id) {
        index = this.prompt.action_group_connector_actions.findIndex(el => el.id === action.id);
      }
      const restActions = this.prompt.action_group_connector_actions.filter(el =>
        el.connectable_type === "ClientAction" && action.connectable_type === "ClientAction"
          ? el.interaction_type !== action.interaction_type ||
            action.args?.survey_option_selected !== el.args?.survey_option_selected
          : el.id !== action.id,
      );

      restActions.splice(index, 0, action);

      this.updatePrompt({
        ...this.prompt,
        action_group_connector_actions: restActions,
      })
        .then(() => this.connectorModal.close?.())
        .catch(_ => null);
    },
    updateActionsOrder(actions) {
      const prompt = {
        ...this.prompt,
        action_group_connector_actions: actions.concat(
          this.actionGroupConnectorActionsGroupedByType.ClientAction || [],
        ),
      };
      this.updatePrompt(prompt);
    },
    deleteAction(action) {
      const prompt = { ...this.prompt };
      if (isRedirectAction(action.actionName)) {
        prompt.actions[action.actionName] = null;
      } else {
        prompt.action_group_connector_actions = this.prompt.action_group_connector_actions.filter(
          el => el.id !== action.id,
        );
      }
      this.updatePrompt(prompt)
        .then(() => this.connectorModal.closeDelete?.())
        .catch(_ => null);
    },
    modifyPrompt(prompt, tag, add = false) {
      this.promptStore.mutateUpdatePrompt({
        prompt: {
          ...prompt,
          tags: [...(prompt?.tags || []).filter(({ id }) => tag.id !== id), ...(add ? [tag] : [])],
        },
        activities: this.promptStore.activities,
      });
    },
    addTagToPromptLocal(prompt, tag) {
      this.modifyPrompt(prompt, tag, true);
    },
    removeTagFromPromptLocal(prompt, tag) {
      this.modifyPrompt(prompt, tag);
    },
    async createTagAndAssignToPrompt(tagProp) {
      const tag = await this.tagsStore.createTag(this.currApp.id, tagProp);
      this.assignTagToPrompt(tag);
    },
    removeTagFromPrompt(tag) {
      this.removeTagFromPromptLocal(this.prompt, tag);
      ApiTags.removeTagFromPrompt(this.prompt.id, tag.id)
        .then(() => null)
        .catch(error => {
          this.addTagToPromptLocal(this.prompt, tag);
          this.toastsStore.create({ type: "error", body: error.message });
        });
    },
    async assignTagToPrompt(tag) {
      this.addTagToPromptLocal(this.prompt, tag);
      ApiTags.assignTagToPrompt(this.prompt.id, tag.id)
        .then(() => null)
        .catch(error => {
          this.removeTagFromPromptLocal(this.prompt, tag);
          this.toastsStore.create({ type: "error", body: error.message });
        });
    },
  },
};
</script>
